"use client";

import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import type { ReactNode } from "react";

export type EmptyStateProps = {
  title: string;
  /** Précision sous le titre : quoi faire, ou pourquoi c'est vide. */
  body?: ReactNode;
  /** Icône. En `inline`, elle remplace celle de l'`Alert`. */
  icon?: ReactNode;
  /** Bouton ou lien de sortie. */
  action?: ReactNode;
  /**
   * `inline` (défaut) — bandeau compact, pour un « aucun résultat » qui
   * s'affiche à la place d'une liste, sous sa barre de filtres.
   *
   * `page` — bloc centré et haut, pour un écran qui n'a rien à montrer du tout.
   *
   * Les deux disent la même chose ; ce qui change est la place disponible.
   */
  variant?: "inline" | "page";
  /**
   * L'état vide résulte d'un filtre ou d'une recherche, et non d'une absence de
   * données. Exposé en `data-after-filter` : les deux causes appellent des
   * messages différents — « aucun résultat, élargissez la recherche » n'est pas
   * « rien ici pour l'instant » — et c'est la distinction que les tests
   * vérifient.
   */
  filtered?: boolean;
  "data-testid"?: string;
};

/**
 * Ce qu'on affiche à la place d'une liste vide.
 *
 * Le monorepo en comptait 15+ formes : `Alert` d'information, `Box` centré,
 * bordure en tirets, bloc illustré. Elles ne divergeaient pas par intention
 * mais par improvisation.
 *
 * Le composant ne fournit **aucun texte** : ni titre par défaut, ni message de
 * repli. Un état vide utile dit ce qui manque *et* quoi faire, ce que seul
 * l'appelant sait — et un défaut générique s'installe durablement là où il
 * aurait fallu écrire une phrase.
 */
export function EmptyState({
  title,
  body,
  icon,
  action,
  variant = "inline",
  filtered,
  "data-testid": dataTestId,
}: EmptyStateProps) {
  const afterFilter =
    filtered === undefined ? undefined : filtered ? "true" : "false";

  if (variant === "page") {
    return (
      <Stack
        data-testid={dataTestId}
        data-after-filter={afterFilter}
        sx={{
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          // Assez haut pour que le bloc se lise comme l'état de la page, et non
          // comme une ligne de liste manquante.
          minHeight: "30vh",
          textAlign: "center",
        }}
      >
        {icon}
        <Typography variant="h5" component="p">
          {title}
        </Typography>
        {body && (
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            {body}
          </Typography>
        )}
        {action && <Stack sx={{ mt: 1 }}>{action}</Stack>}
      </Stack>
    );
  }

  return (
    <Alert
      severity="info"
      icon={icon}
      data-testid={dataTestId}
      data-after-filter={afterFilter}
    >
      <Stack spacing={0.5} sx={{ alignItems: "flex-start" }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {body && (
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {body}
          </Typography>
        )}
        {action}
      </Stack>
    </Alert>
  );
}
